export type McpClientId = "openclaw" | "codex" | "claude-code" | "cursor" | "generic";

export interface McpClientOption {
  id: McpClientId;
  label: string;
  description: string;
}

export const MCP_CLIENTS: readonly McpClientOption[] = [
  {
    id: "openclaw",
    label: "OpenClaw",
    description: "Add Podwise to your OpenClaw user profile.",
  },
  {
    id: "codex",
    label: "Codex",
    description: "Configure Podwise as a user-level Streamable HTTP server.",
  },
  {
    id: "claude-code",
    label: "Claude Code",
    description: "Add Podwise to Claude Code for your user account.",
  },
  {
    id: "cursor",
    label: "Cursor",
    description: "Reference the token from Cursor's MCP configuration.",
  },
  {
    id: "generic",
    label: "Generic HTTP",
    description: "Use these values with any Streamable HTTP MCP client.",
  },
] as const;

const TOKEN_PLACEHOLDER = "<YOUR_TOKEN>";

const normalizeServerUrl = (serverUrl: string) => serverUrl.trim().replace(/\/+$/, "");

const shellSingleQuote = (value: string) => `'${value.replaceAll("'", `'"'"'`)}'`;

export function getMcpClientSnippet(
  clientId: McpClientId,
  serverUrl: string,
): string {
  const url = normalizeServerUrl(serverUrl);
  const exportToken = `export PODWISE_MCP_TOKEN=${shellSingleQuote(TOKEN_PLACEHOLDER)}`;

  switch (clientId) {
    case "openclaw":
      return `${exportToken}

openclaw mcp add podwise \\
  --url ${shellSingleQuote(url)} \\
  --transport streamable-http \\
  --header 'Authorization=Bearer \${PODWISE_MCP_TOKEN}' \\
  --timeout 120 \\
  --connect-timeout 30 \\
  --no-probe

openclaw mcp doctor podwise --probe`;
    case "codex":
      return `${exportToken}

# Add to ~/.codex/config.toml
[mcp_servers.podwise]
url = ${JSON.stringify(url)}
bearer_token_env_var = "PODWISE_MCP_TOKEN"
tool_timeout_sec = 120`;
    case "claude-code":
      return `${exportToken}

claude mcp add --transport http --scope user podwise ${shellSingleQuote(url)} \\
  --header 'Authorization: Bearer \${PODWISE_MCP_TOKEN}'`;
    case "cursor":
      return `${exportToken}

// Add to ~/.cursor/mcp.json, then start Cursor from this environment.
${JSON.stringify(
  {
    mcpServers: {
      podwise: {
        url,
        headers: {
          Authorization: "Bearer ${env:PODWISE_MCP_TOKEN}",
        },
      },
    },
  },
  null,
  2,
)}`;
    case "generic":
      return JSON.stringify(
        {
          transport: "streamable-http",
          url,
          headers: {
            Authorization: `Bearer ${TOKEN_PLACEHOLDER}`,
          },
        },
        null,
        2,
      );
  }
}
